/* RESPONSIBLE TEAM: team-reporting */

import type {
  Cell,
  ChartConfig,
  ChartData,
  ChartDataResponse,
  DataRow,
  DualQueryCell,
  FlatSummaryRow,
  GroupedData,
  MetricMetadata,
  MetricUnit,
  RenderContext,
  SummaryCellValue,
  TimeInterval,
} from '@/features/reporting-charts/types';
import {
  isDimensionCell,
  isDualQueryCell,
  isGroupedData,
  isGroupedSummaryRow,
} from '@/features/reporting-charts/types';
import {
  findPeriod,
  formatDimensionDisplayValue,
  formatTimeDimensionLabel,
  formatValue,
  getDisplayNameWithAggregation,
} from '@/features/reporting-charts/transforms/utils';

export interface TableColumn {
  key: string;
  label: string;
  align: 'left' | 'right';
}

export type TargetStatus = 'bg-success-container' | 'bg-error-container';

export interface TableCell {
  value: string;
  align: 'left' | 'right';
  fractionLabel?: string;
  comparison?: {
    formattedDelta: string;
    changeDirection: 'up' | 'down';
    improvementDirection: MetricMetadata['improvementDirection'];
  };
  targetStatus?: TargetStatus;
}

export interface TableData {
  columns: TableColumn[];
  rows: TableCell[][];
  summaryRow: TableCell[] | null;
}

export function buildTableData(
  data: ChartDataResponse,
  config: ChartConfig,
  renderContext: RenderContext,
): TableData {
  const current = findPeriod(data, 'current');

  if (!current || !isGroupedData(current.data) || current.data.length === 0) {
    return { columns: [], rows: [], summaryRow: null };
  }

  const { columns: columnNames, metricMetadata, summaryRow } = current.metadata;

  // Segmented data needs pivoting: each segment value becomes its own column
  const segmentIndex = config.segmentBy != null ? columnNames.indexOf(config.segmentBy) : -1;
  if (segmentIndex !== -1) {
    return buildPivotedTableData(current, config, renderContext, segmentIndex);
  }

  const showSummaryRow = config.visualizationOptions?.showSummaryRow ?? false;
  const showTimeComparison = config.visualizationOptions?.showTimeComparison ?? false;
  const target = config.visualizationOptions?.target;

  const isTimeBased = columnNames[0] === 'time';
  const timeInterval = config.viewByTimeInterval;

  const metricIds = new Set(metricMetadata.map((m) => m.metricId));

  const displayNameMap = new Map<string, string>();
  for (const series of config.chartSeries ?? []) {
    const displayName = getDisplayNameWithAggregation(series, series.metricDisplayName);
    if (displayName) {
      displayNameMap.set(series.metricId, displayName);
    }
  }

  const columns: TableColumn[] = columnNames.map((name, index) => ({
    key: name,
    label: metricIds.has(name)
      ? (displayNameMap.get(name) ?? formatColumnLabel(name))
      : index === 0 && config.viewByDisplay
        ? config.viewByDisplay
        : formatColumnLabel(name, index === 0 && isTimeBased ? timeInterval : undefined),
    align: metricIds.has(name) ? 'right' : 'left',
  }));

  const unitMap = new Map<string, MetricUnit>();
  const improvementMap = new Map<string, MetricMetadata['improvementDirection']>();
  for (const meta of metricMetadata) {
    unitMap.set(meta.metricId, meta.unit);
    improvementMap.set(meta.metricId, meta.improvementDirection);
  }

  const previous = showTimeComparison ? findPeriod(data, 'previous') : null;
  const previousRows = previous && isGroupedData(previous.data) ? previous.data : null;

  // Compute comparisons BEFORE reversing rows (index-aligned with backend order)
  let rows = current.data.map((row, rowIndex) => {
    const previousRow =
      previousRows && rowIndex < previousRows.length ? previousRows[rowIndex] : null;
    return transformRow(
      row,
      columnNames,
      unitMap,
      improvementMap,
      renderContext,
      isTimeBased ? timeInterval : undefined,
      previousRow,
      target,
    );
  });

  // Time-based tables show newest first
  if (isTimeBased) {
    rows = rows.reverse();
  }

  const flatSummaryRow = summaryRow && isGroupedSummaryRow(summaryRow) ? summaryRow[0] : summaryRow;
  const previousRawSummary = showTimeComparison ? (previous?.metadata.summaryRow ?? null) : null;
  const previousSummaryRow =
    previousRawSummary && isGroupedSummaryRow(previousRawSummary)
      ? previousRawSummary[0]
      : previousRawSummary;
  const transformedSummaryRow =
    showSummaryRow && flatSummaryRow
      ? transformSummaryRow(
          flatSummaryRow,
          columnNames,
          unitMap,
          improvementMap,
          renderContext,
          previousSummaryRow,
        )
      : null;

  return { columns, rows, summaryRow: transformedSummaryRow };
}

function buildPivotedTableData(
  current: ChartData,
  config: ChartConfig,
  renderContext: RenderContext,
  segmentIndex: number,
): TableData {
  const { columns: columnNames, metricMetadata } = current.metadata;
  const data = current.data as GroupedData;
  const metricIds = new Set(metricMetadata.map((m) => m.metricId));

  // Classify column indices
  const groupByIndices: number[] = [];
  const metricIndices: number[] = [];
  for (let i = 0; i < columnNames.length; i++) {
    if (i === segmentIndex) continue;
    if (metricIds.has(columnNames[i])) {
      metricIndices.push(i);
    } else {
      groupByIndices.push(i);
    }
  }

  // Collect unique segment values in first-occurrence order
  const isSegmentTimeBased = columnNames[segmentIndex] === 'time';
  const segmentTimeInterval = config.segmentByTimeInterval;
  const segmentValues: string[] = [];
  const segmentRawValues = new Map<string, string>();
  const segmentLabels = new Map<string, string>();
  const segmentValueSet = new Set<string>();
  for (const row of data) {
    const val = cellDisplayString(row[segmentIndex]);
    if (!segmentValueSet.has(val)) {
      segmentValueSet.add(val);
      segmentValues.push(val);
      const cell = row[segmentIndex];
      segmentRawValues.set(val, isDimensionCell(cell) ? String(cell.rawValue) : val);
      if (
        isSegmentTimeBased &&
        segmentTimeInterval &&
        isDimensionCell(cell) &&
        typeof cell.rawValue === 'number'
      ) {
        segmentLabels.set(
          val,
          formatTimeDimensionLabel(cell.rawValue, segmentTimeInterval, renderContext),
        );
      }
    }
  }

  // Sort segments: numeric ascending (e.g. ratings 1→5), "Other" last,
  // non-numeric preserve API order (value-descending)
  sortSegmentValues(segmentValues, segmentRawValues);

  // Group rows by non-segment dimensions
  const groups = new Map<string, Map<string, DataRow>>();
  const groupDimCells = new Map<string, Cell[]>();
  for (const row of data) {
    const keyParts: string[] = [];
    const dims: Cell[] = [];
    for (const gi of groupByIndices) {
      const cell = row[gi];
      keyParts.push(isDimensionCell(cell) ? String(cell.rawValue) : cellDisplayString(cell));
      dims.push(cell);
    }
    const groupKey = keyParts.join('|');
    const segValue = cellDisplayString(row[segmentIndex]);

    let group = groups.get(groupKey);
    if (!group) {
      group = new Map();
      groups.set(groupKey, group);
      groupDimCells.set(groupKey, dims);
    }
    group.set(segValue, row);
  }

  // Build columns: [group-by dims] + [one per segment value per metric]
  const isTimeBased = columnNames[groupByIndices[0]] === 'time';
  const timeInterval = config.viewByTimeInterval;

  const displayNameMap = new Map<string, string>();
  for (const series of config.chartSeries ?? []) {
    const displayName = getDisplayNameWithAggregation(series, series.metricDisplayName);
    if (displayName) {
      displayNameMap.set(series.metricId, displayName);
    }
  }

  const columns: TableColumn[] = groupByIndices.map((gi, i) => ({
    key: columnNames[gi],
    label:
      i === 0 && config.viewByDisplay
        ? config.viewByDisplay
        : formatColumnLabel(
            columnNames[gi],
            gi === groupByIndices[0] && isTimeBased ? timeInterval : undefined,
          ),
    align: 'left' as const,
  }));

  const hasSingleMetric = metricIndices.length === 1;
  for (const segValue of segmentValues) {
    const segLabel = segmentLabels.get(segValue) ?? segValue;
    for (const mi of metricIndices) {
      const metricLabel = displayNameMap.get(columnNames[mi]) ?? formatColumnLabel(columnNames[mi]);
      columns.push({
        key: hasSingleMetric ? segValue : `${segValue}_${columnNames[mi]}`,
        label: hasSingleMetric ? segLabel : `${segLabel} – ${metricLabel}`,
        align: 'right' as const,
      });
    }
  }

  // Build unit + improvement maps
  const unitMap = new Map<string, MetricUnit>();
  const improvementMap = new Map<string, MetricMetadata['improvementDirection']>();
  for (const meta of metricMetadata) {
    unitMap.set(meta.metricId, meta.unit);
    improvementMap.set(meta.metricId, meta.improvementDirection);
  }

  const target = config.visualizationOptions?.target;

  // Build pivoted rows
  let rows: TableCell[][] = [];
  for (const [groupKey, segmentMap] of groups) {
    const dims = groupDimCells.get(groupKey);
    if (!dims) continue;
    const row: TableCell[] = [];

    // Dimension cells
    for (let i = 0; i < dims.length; i++) {
      const cell = dims[i];
      const isTimeCol = i === 0 && isTimeBased && timeInterval;
      if (isDimensionCell(cell) && isTimeCol && typeof cell.rawValue === 'number') {
        row.push({
          value: formatTimeDimensionLabel(cell.rawValue, timeInterval, renderContext),
          align: 'left',
        });
      } else if (isDimensionCell(cell)) {
        row.push({ value: formatDimensionDisplayValue(cell), align: 'left' });
      } else {
        row.push({ value: cellDisplayString(cell), align: 'left' });
      }
    }

    // Metric cells for each segment × metric
    for (const segValue of segmentValues) {
      const segRow = segmentMap.get(segValue);
      for (const mi of metricIndices) {
        if (!segRow) {
          row.push({ value: '-', align: 'right' });
          continue;
        }
        const cell = segRow[mi];
        const colName = columnNames[mi];
        const unit = unitMap.get(colName) ?? 'value';
        const numericValue = getNumericValue(cell);
        const targetStatus = computeTargetStatus(
          numericValue,
          target,
          improvementMap.get(colName) ?? 'increasing',
        );
        if (isDualQueryCell(cell)) {
          row.push({
            value: formatValue(cell.value, unit, renderContext),
            align: 'right',
            fractionLabel:
              unit === 'percent' ? formatFractionLabel(cell, renderContext.locale) : undefined,
            targetStatus,
          });
        } else if (typeof cell === 'number') {
          row.push({
            value: formatValue(cell, unit, renderContext),
            align: 'right',
            targetStatus,
          });
        } else {
          row.push({ value: cell === null ? '-' : cellDisplayString(cell), align: 'right' });
        }
      }
    }

    rows.push(row);
  }

  if (isTimeBased) {
    rows = rows.reverse();
  }

  const showSummaryRow = config.visualizationOptions?.showSummaryRow ?? false;
  const { summaryRow } = current.metadata;
  let transformedSummaryRow: TableCell[] | null = null;

  if (showSummaryRow && summaryRow && isGroupedSummaryRow(summaryRow)) {
    // Build a map from segment display value to its metric values
    const summaryBySegment = new Map<string, SummaryCellValue[]>();
    for (const segmentRow of summaryRow) {
      const segCell = segmentRow[0];
      const displayValue =
        segCell === null
          ? ''
          : isDimensionCell(segCell)
            ? segCell.displayValue
            : typeof segCell === 'number'
              ? String(segCell)
              : '';
      summaryBySegment.set(displayValue, segmentRow.slice(1));
    }

    transformedSummaryRow = [];

    // Empty cells for group-by dimension columns
    for (const _ of groupByIndices) {
      transformedSummaryRow.push({ value: '', align: 'left' });
    }

    // Metric values per segment in segment order
    for (const segValue of segmentValues) {
      const metricValues = summaryBySegment.get(segValue);
      for (let mi = 0; mi < metricIndices.length; mi++) {
        const cell = metricValues?.[mi];
        const unit = unitMap.get(columnNames[metricIndices[mi]]) ?? 'value';

        if (cell != null && isDualQueryCell(cell)) {
          transformedSummaryRow.push({
            value: formatValue(cell.value, unit, renderContext),
            align: 'right',
            fractionLabel:
              unit === 'percent' ? formatFractionLabel(cell, renderContext.locale) : undefined,
          });
        } else if (typeof cell === 'number') {
          transformedSummaryRow.push({
            value: formatValue(cell, unit, renderContext),
            align: 'right',
          });
        } else {
          transformedSummaryRow.push({ value: '-', align: 'right' });
        }
      }
    }
  }

  return { columns, rows, summaryRow: transformedSummaryRow };
}

function transformRow(
  row: DataRow,
  columnNames: string[],
  unitMap: Map<string, MetricUnit>,
  improvementMap: Map<string, MetricMetadata['improvementDirection']>,
  renderContext: RenderContext,
  timeInterval?: TimeInterval,
  previousRow?: DataRow | null,
  target?: { value: number },
): TableCell[] {
  return row.map((cell, index) => {
    const columnName = columnNames[index];
    const unit = unitMap.get(columnName);
    const isMetric = unit !== undefined;
    const isTimeColumn = index === 0 && columnName === 'time' && timeInterval;

    if (isDimensionCell(cell)) {
      if (isTimeColumn && typeof cell.rawValue === 'number') {
        return {
          value: formatTimeDimensionLabel(cell.rawValue, timeInterval, renderContext),
          align: 'left' as const,
        };
      }
      return { value: formatDimensionDisplayValue(cell), align: 'left' as const };
    }

    const numericValue = getNumericValue(cell);
    const improvementDirection = improvementMap.get(columnName) ?? 'increasing';

    const comparison =
      isMetric && previousRow
        ? computeCellComparison(
            numericValue,
            getNumericValue(previousRow[index]),
            unit,
            improvementDirection,
            renderContext,
          )
        : undefined;

    const targetStatus = isMetric
      ? computeTargetStatus(numericValue, target, improvementDirection)
      : undefined;

    if (isDualQueryCell(cell)) {
      return {
        value: formatValue(cell.value, unit ?? 'value', renderContext),
        align: 'right' as const,
        fractionLabel:
          unit === 'percent' ? formatFractionLabel(cell, renderContext.locale) : undefined,
        comparison,
        targetStatus,
      };
    }

    if (typeof cell === 'number') {
      return {
        value: formatValue(cell, unit ?? 'value', renderContext),
        align: 'right' as const,
        comparison,
        targetStatus,
      };
    }

    return {
      value: cell ?? '-',
      align: isMetric ? ('right' as const) : ('left' as const),
    };
  });
}

function transformSummaryRow(
  summaryRow: FlatSummaryRow,
  columnNames: string[],
  unitMap: Map<string, MetricUnit>,
  improvementMap: Map<string, MetricMetadata['improvementDirection']>,
  renderContext: RenderContext,
  previousSummaryRow: FlatSummaryRow | null,
): TableCell[] {
  return summaryRow.map((cell, index) => {
    const columnName = columnNames[index];
    const unit = unitMap.get(columnName);
    const isMetric = unit !== undefined;

    const previousCell = previousSummaryRow?.[index];
    const comparison =
      isMetric && previousCell != null
        ? computeCellComparison(
            getNumericValue(cell),
            getNumericValue(previousCell),
            unit,
            improvementMap.get(columnName) ?? 'increasing',
            renderContext,
          )
        : undefined;

    if (isDualQueryCell(cell)) {
      return {
        value: formatValue(cell.value, unit ?? 'value', renderContext),
        align: 'right' as const,
        fractionLabel:
          unit === 'percent' ? formatFractionLabel(cell, renderContext.locale) : undefined,
        comparison,
      };
    }

    if (typeof cell === 'number') {
      return {
        value: formatValue(cell, unit ?? 'value', renderContext),
        align: 'right' as const,
        comparison,
      };
    }

    return {
      value: '',
      align: isMetric ? ('right' as const) : ('left' as const),
    };
  });
}

function sortSegmentValues(segments: string[], rawValues: Map<string, string>): void {
  const toNumber = (s: string): number => Number(rawValues.get(s) ?? s);

  if (segments.every((s) => Number.isFinite(toNumber(s)))) {
    segments.sort((a, b) => toNumber(a) - toNumber(b));
  }
}

function cellDisplayString(cell: Cell): string {
  if (cell === null) return '';
  if (typeof cell === 'number') return String(cell);
  if (typeof cell === 'string') return cell;
  if (isDimensionCell(cell)) return cell.displayValue;
  return String(cell.value);
}

function getNumericValue(cell: Cell | SummaryCellValue | undefined): number | null {
  if (cell === null || cell === undefined) return null;
  if (typeof cell === 'number') return cell;
  if (typeof cell === 'object' && 'numeratorValue' in cell) return cell.value;
  return null;
}

function computeCellComparison(
  currentValue: number | null,
  previousValue: number | null,
  unit: MetricUnit,
  improvementDirection: MetricMetadata['improvementDirection'],
  renderContext: RenderContext,
): TableCell['comparison'] | undefined {
  if (currentValue === null || previousValue === null) return undefined;
  const delta = currentValue - previousValue;
  if (delta === 0) return undefined;

  return {
    formattedDelta: formatValue(Math.abs(delta), unit, renderContext),
    changeDirection: delta > 0 ? 'up' : 'down',
    improvementDirection,
  };
}

function formatFractionLabel(cell: DualQueryCell, locale: string): string {
  if (cell.denominatorValue === 0) return '';

  const fmt = new Intl.NumberFormat(locale);
  return `(${fmt.format(cell.numeratorValue)}/${fmt.format(cell.denominatorValue)})`;
}

function computeTargetStatus(
  value: number | null,
  target: { value: number } | undefined,
  improvementDirection: MetricMetadata['improvementDirection'],
): TargetStatus | undefined {
  if (value === null || !target) return undefined;

  if (improvementDirection === 'increasing') {
    return value >= target.value ? 'bg-success-container' : 'bg-error-container';
  }
  return value <= target.value ? 'bg-success-container' : 'bg-error-container';
}

const INTERVAL_LABELS: Record<TimeInterval, string> = {
  hour: 'Hour',
  day: 'Day',
  week: 'Week',
  month: 'Month',
  day_of_week: 'Day of Week',
  hour_of_day: 'Hour of Day',
};

function formatColumnLabel(name: string, timeInterval?: TimeInterval): string {
  if (name === 'time' && timeInterval) {
    return INTERVAL_LABELS[timeInterval];
  }

  return name
    .split(/[._]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
